/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { useTranslation } from 'react-i18next';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getProjectStatistics } from 'apis/dashboard.api';
import Loading from 'components/Loading';
import { formatShowAppliedFilters, statusColors } from 'utils/func/dashboard';
import DashboardFilter from '../DashboardFilter';

export default function ProjectChartVerticalBar({ programs, organizations }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoadedFilterFromLocalStorage, setIsLoadedFilterFromLocalStorage] = useState(false);

  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [filters, setFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });
  const [appliedFilters, setAppliedFilters] = useState({
    yearProposed: [],
    organizationId: [],
    programId: [],
  });
  const [showFilterDialog, setShowFilterDialog] = useState(false);

  const documentStyle = getComputedStyle(document.documentElement);
  const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary');

  // Check if any filters are applied
  const hasActiveFilters =
    appliedFilters.yearProposed?.length > 0 ||
    appliedFilters.organizationId?.length > 0 ||
    appliedFilters.programId?.length > 0;

  const { data: statisticsData, isLoading } = useQuery(
    ['projectStatistics', appliedFilters],
    () => getProjectStatistics(appliedFilters),
    {
      refetchOnWindowFocus: false,
      enabled: isLoadedFilterFromLocalStorage,
    }
  );

  // Process statistics data to create chart data
  useEffect(() => {
    if (statisticsData?.data?.data && Array.isArray(statisticsData.data.data)) {
      const statusMapping = {
        ongoingProject: t('dashboard.projectSubmit.label.ongoingProject'),
        midtermQualifiedProject: t('dashboard.projectSubmit.label.midtermQualifiedProject'),
        midtermNotQualifiedProject: t('dashboard.projectSubmit.label.midtermNotQualifiedProject'),
        acceptanceQualifiedProject: t('dashboard.projectSubmit.label.acceptanceQualifiedProject'),
        acceptanceNotQualifiedProject: t(
          'dashboard.projectSubmit.label.acceptanceNotQualifiedProject'
        ),
        overdueProject: t('dashboard.projectSubmit.label.overdueProject'),
      };

      const labels = [];
      const dataValues = [];
      const colors = Object.values(statusColors.submit);

      const itemsData = [];
      statisticsData?.data?.data.forEach((item) => {
        if (statusMapping[item.status]) {
          labels.push(statusMapping[item.status]);
          dataValues.push(item.count);
          itemsData.push(item);
        }
      });

      const data = {
        labels,
        datasets: [
          {
            label: t('dashboard.amountOfProjects'),
            data: dataValues,
            backgroundColor: colors.slice(0, dataValues.length),
            borderColor: colors.slice(0, dataValues.length),
            borderWidth: 1,
          },
        ],
      };

      const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          datalabels: {
            formatter: (value, ctx) => {
              const item = itemsData[ctx.dataIndex];
              if (item.percentage < 0) {
                return null;
              }
              return `${value}`;
            },
            color: textColorSecondary,
            font: {
              weight: 'bold',
              size: 11,
            },
            anchor: 'end',
            align: 'top',
          },
          tooltip: {
            callbacks: {
              label(context) {
                const item = itemsData[context.dataIndex];
                return `${t('dashboard.amountOfProjects')}: ${
                  item.count
                } (${item.percentage.toFixed(1)}%)`;
              },
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            suggestedMax: Math.max(...dataValues) * 1.1,
            ticks: {
              stepSize: 1,
            },
          },
          x: {
            ticks: {
              maxRotation: 45,
              minRotation: 0,
            },
          },
        },
        onHover: (event, activeElements) => {
          const canvas = event.native.target;
          canvas.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
        },
        onClick: (event, elements) => {
          if (elements.length > 0) {
            const clickedIndex = elements[0].index;
            const clickedItem = itemsData[clickedIndex];

            if (!clickedItem) return;

            const projectSubmitFilters = {
              yearProposed: appliedFilters?.yearProposed ?? [],
              organizationId: appliedFilters?.organizationId ?? [],
              programId: appliedFilters?.programId ?? [],
              projectStatus: [clickedItem.status],
            };

            localStorage.setItem(
              'admin-projects-submit-filters',
              JSON.stringify(projectSubmitFilters)
            );
            navigate('/projects-submit');
          }
        },
        interaction: {
          intersect: false,
        },
      };

      setChartData(data);
      setChartOptions(options);
    }
  }, [appliedFilters, navigate, statisticsData, t, textColorSecondary]);

  useEffect(() => {
    setIsLoadedFilterFromLocalStorage(true);
    const savedFilters = localStorage.getItem('admin-dashboard-projects-submit-filters');
    if (!savedFilters) return;

    setFilters(JSON.parse(savedFilters));
    setAppliedFilters(JSON.parse(savedFilters));
  }, []);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="card p-3 md:p-5">
      {/* Header Section */}
      <div className="card-header border-bottom-1 surface-border pb-3 mb-4">
        <div className="flex justify-content-between align-items-center">
          <h3 className="text-2xl text-900 font-semibold m-0">
            {t('dashboard.projectSubmit.title')}
          </h3>

          {/* Filter Button - moved to header */}
          <Button
            icon="pi pi-filter"
            label={t('dashboard.filter.label.button')}
            severity={hasActiveFilters ? 'success' : 'secondary'}
            outlined={!hasActiveFilters}
            onClick={() => setShowFilterDialog(true)}
            className="p-button-sm"
          />
        </div>
      </div>

      <DashboardFilter
        showFilterDialog={showFilterDialog}
        onHide={() => setShowFilterDialog(false)}
        filters={filters}
        setFilters={setFilters}
        setAppliedFilters={setAppliedFilters}
        organizations={organizations}
        programs={programs}
        localStorageKey="admin-dashboard-projects-submit-filters"
        isSelectOne={{
          yearProposed: false,
          organizationId: false,
          programId: false,
        }}
      />

      {/* Chart Section */}
      <div className="flex flex-column align-items-center md:px-4 md:pb-4 gap-2">
        {formatShowAppliedFilters(appliedFilters, organizations, programs, t)}

        <Chart
          type="bar"
          data={chartData}
          options={chartOptions}
          plugins={[ChartDataLabels]}
          className="w-full"
          style={{ height: '400px' }}
        />
      </div>
    </div>
  );
}

ProjectChartVerticalBar.propTypes = {
  programs: PropTypes.array.isRequired,
  organizations: PropTypes.array.isRequired,
};
